import React, { useEffect, useMemo, useState } from 'react';
import { FiMapPin, FiSearch, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import HotelCard from '../components/HotelCard';
import { hotelService } from '../services/hotelService';
import { EmptyState, ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';

const HotelSearchPage = () => {
  const { t } = useTranslation();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    city: '',
    guests: ''
  });

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        setLoading(true);
        const data = await hotelService.getAllHotels();
        setHotels(data || []);
        setError('');
      } catch (requestError) {
        console.error('Hotel list error:', requestError);
        setError(t('design.hotelBrowse.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, [t]);

  const filteredHotels = useMemo(() => {
    return hotels.filter((hotel) => {
      const cityMatch = !filters.city || `${hotel.hotelCity || ''} ${hotel.district || ''}`.toLowerCase().includes(filters.city.toLowerCase());
      const guestMatch = !filters.guests || Number(filters.guests) <= Number(hotel.maxGuests || filters.guests);
      return cityMatch && guestMatch;
    });
  }, [filters.city, filters.guests, hotels]);

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.hotelBrowse.loading')} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorState title={t('design.hotelBrowse.unavailableTitle')} message={error} action={<button type="button" onClick={() => window.location.reload()} className="btn-primary">{t('common.tryAgain')}</button>} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.hotelBrowse.heroEyebrow')}
        title={t('design.hotelBrowse.heroTitle')}
        description={t('design.hotelBrowse.heroDescription')}
        aside={
          <div className="soft-panel p-6 sm:p-8">
            <p className="eyebrow">{t('design.hotelBrowse.filtersEyebrow')}</p>
            <div className="mt-6 space-y-4">
              <div>
                <label className="field-label">{t('design.hotelBrowse.cityLabel')}</label>
                <div className="relative">
                  <FiMapPin className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={filters.city}
                    onChange={(event) => setFilters((value) => ({ ...value, city: event.target.value }))}
                    placeholder={t('design.hotelBrowse.cityPlaceholder')}
                    className="input-shell pl-11"
                  />
                </div>
              </div>
              <div>
                <label className="field-label">{t('design.hotelBrowse.guestLabel')}</label>
                <div className="relative">
                  <FiUsers className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number"
                    min="1"
                    value={filters.guests}
                    onChange={(event) => setFilters((value) => ({ ...value, guests: event.target.value }))}
                    placeholder="2"
                    className="input-shell pl-11"
                  />
                </div>
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <SectionHeading
          eyebrow={t('design.hotelBrowse.resultsEyebrow')}
          title={t('design.hotelBrowse.resultsTitle', { count: filteredHotels.length })}
          description={t('design.hotelBrowse.resultsDescription')}
          action={
            <button type="button" className="btn-ghost">
              <FiSearch className="h-4 w-4" />
              {t('design.hotelBrowse.liveFilter')}
            </button>
          }
        />

        {filteredHotels.length ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredHotels.map((hotel) => (
              <HotelCard key={hotel.id} hotel={hotel} />
            ))}
          </div>
        ) : (
          <EmptyState title={t('design.hotelBrowse.noResultsTitle')} description={t('design.hotelBrowse.noResultsDescription')} />
        )}
      </section>
    </Layout>
  );
};

export default HotelSearchPage;
